import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Icon } from '@metrodev/core-web';
import { kitchenOrdersApi } from '../../api';
import { useAuth } from '../../hooks/useAuth';
import { isCocina } from '../../lib/permissions';
import { countKitchenOrderAlerts } from '../../lib/kitchenOrderAlerts';

/** Misma clave que «Mis pedidos» sin filtro de sede, así la campanita y la tarjeta reusan su caché. */
function useKitchenOrderAlerts() {
  const { user } = useAuth();
  const enabled = isCocina(user);
  const { data } = useQuery({
    queryKey: ['kitchen-orders-tracking', { siteId: '' }],
    queryFn: () => kitchenOrdersApi.tracking({}),
    enabled,
    refetchInterval: 60_000,
  });
  return { enabled, counts: countKitchenOrderAlerts(data) };
}

/** #262: campanita del cocinero con el contador de pedidos observados por Compras (a revisar). */
export function KitchenOrdersBell() {
  const { enabled, counts } = useKitchenOrderAlerts();
  if (!enabled) return null;
  const label = counts.observed > 0 ? `${counts.observed} pedido${counts.observed === 1 ? '' : 's'} observado${counts.observed === 1 ? '' : 's'} para revisar` : 'Pedidos observados: ninguno';
  return (
    <Link
      to="/kitchen-orders/mine?filter=observed"
      aria-label={label}
      title={label}
      style={{ position: 'relative', display: 'flex', alignItems: 'center', padding: 6, borderRadius: 8, color: counts.observed > 0 ? 'var(--color-warning-strong)' : 'var(--color-text-muted)' }}
    >
      <Icon name="bell" size={20} />
      {counts.observed > 0 && (
        <span aria-hidden="true" style={{ position: 'absolute', top: 0, right: 0, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 8, background: 'var(--color-warning)', color: 'var(--color-on-primary)', fontSize: 10.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {counts.observed > 99 ? '99+' : counts.observed}
        </span>
      )}
    </Link>
  );
}

/** #262: tarjeta «Pedidos a revisar / observados» con acceso directo a «Mis pedidos» ya filtrado. */
export function OrdersToReviewCard() {
  const { enabled, counts } = useKitchenOrderAlerts();
  if (!enabled) return null;
  const item = (to: string, label: string, value: number, danger = false) => (
    <Link to={to} style={{ textDecoration: 'none', color: 'var(--color-text)', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 110 }}>
      <span style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, color: danger && value > 0 ? 'var(--color-danger-strong)' : 'var(--color-text)' }}>{value}</span>
      <span style={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>{label}</span>
    </Link>
  );
  return (
    <section
      aria-label="Pedidos a revisar"
      style={{ border: '1px solid var(--color-border-divider)', borderLeft: `4px solid ${counts.observed > 0 ? 'var(--color-warning)' : 'var(--color-success)'}`, borderRadius: 10, padding: '14px 18px', marginBottom: 24, background: 'var(--color-surface)' }}
    >
      <h2 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 10px' }}>
        {counts.observed > 0 ? '⚠ Pedidos a revisar' : '✓ Pedidos a revisar'}
      </h2>
      <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
        {item('/kitchen-orders/mine?filter=observed', 'Observados por Compras', counts.observed)}
        {item('/kitchen-orders/mine?filter=urgent', 'Urgentes', counts.urgent, true)}
        {item('/kitchen-orders/mine?filter=upcoming', 'Próximos', counts.upcoming)}
        {item('/kitchen-orders/mine?filter=finalized', 'Finalizados', counts.finalized)}
      </div>
    </section>
  );
}
